import { Fragment } from 'react';
import type { Block, Inline } from '../lib/legalText';

/** The parsed legal Markdown (`lib/legalText.ts`) in the site's type scale. */
export function LegalDocument({ blocks }: { blocks: Block[] }) {
  return (
    <div className="flex flex-col gap-3">
      {blocks.map((block, index) => (
        <BlockView key={index} block={block} />
      ))}
    </div>
  );
}

function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case 'heading':
      return <h3 className="font-heading text-ink mt-4 text-lg font-bold">{block.text}</h3>;
    case 'paragraph':
      return (
        <p>
          {block.lines.map((line, index) => (
            <Fragment key={index}>
              {index > 0 ? <br /> : null}
              <Line parts={line} />
            </Fragment>
          ))}
        </p>
      );
    case 'list':
      return (
        <ul className="flex list-disc flex-col gap-1 pl-5">
          {block.items.map((item, index) => (
            <li key={index}>
              <Line parts={item} />
            </li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-line border-b">
                {block.head.map((cell, index) => (
                  <th key={index} className="text-ink py-2 pr-4 font-semibold">
                    <Line parts={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-line border-b last:border-0">
                  {row.map((cell, index) => (
                    <td key={index} className="py-2 pr-4 align-top">
                      <Line parts={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

function Line({ parts }: { parts: Inline[] }) {
  return (
    <>
      {parts.map((part, index) => {
        switch (part.kind) {
          case 'text':
            return <Fragment key={index}>{part.text}</Fragment>;
          case 'bold':
            return (
              <strong key={index} className="text-ink font-semibold">
                {part.text}
              </strong>
            );
          case 'link':
            return (
              <a key={index} href={part.href} className="text-teal underline">
                {part.text}
              </a>
            );
          case 'missing':
            // § 13 — staging only: production drops the line instead.
            return (
              <span key={index} className="bg-lime-tint text-lime-ink rounded px-1 font-mono text-sm">
                [{part.path}]
              </span>
            );
        }
      })}
    </>
  );
}
